import React, { BaseSyntheticEvent, useState } from 'react';
import {
    useCommunityComparisonChartDataQuery,
    useCommunityComparisonSamplesQuery,
} from 'src/app/ecology-hub/state/api/ecologyHubGraphSlice';
import { getCurrentCommunityComparisonUIState } from 'src/app/ecology-hub/state/community-comparison/communityComparisonSelector';
import Loader from 'src/shared/components/loader/Loader';
import { useAppSelector } from 'src/store';
import Table from 'src/shared/components/table/Table';
import QuestionButtonHelp from 'src/shared/components/question-button-help/QuestionButtonHelp';
import useProjectFilters from 'src/shared/hooks/useProjectFilters';
import { Popover, PopoverAnchor, PopoverContent } from 'src/shared/components/ui/popover';
import SpeciesInfoPopup from 'src/shared/components/species-info-popup/SpeciesInfoPopup';
import PercentageBar from '../../percentage-bar/PercentageBar';
import styles from '../CommunityComparisonAside.module.scss';

const AsideBottomContent = () => {
    const { currentProjectId, currentProjectSelectedHabitatAssayType: habitatAssayType } = useProjectFilters();
    const [openedPopoverId, setOpenedPopoverId] = useState('');
    const uiState = useAppSelector(getCurrentCommunityComparisonUIState);

    const {
        filters: { samplingYear, selectedSampleId, selectedSampleGroup },
    } = uiState;

    const { currentData: chartData, isFetching: isFetchingChartData } = useCommunityComparisonChartDataQuery(
        {
            projectId: currentProjectId || '',
            habitatAssay: habitatAssayType?.key || '',
        },
        {
            skip: !currentProjectId || !habitatAssayType,
        }
    );

    let querySampleIds = [];

    if (selectedSampleId) {
        querySampleIds = [selectedSampleId];
    } else {
        const selectedYearData = chartData?.communityComparisonChartData.data[0].years[Number(samplingYear)].data;
        querySampleIds =
            selectedYearData?.find(entry => entry.variable.value === selectedSampleGroup)?.points.map(point => point.sampleId) || [];
    }

    const { currentData, isFetching } = useCommunityComparisonSamplesQuery(
        {
            projectId: currentProjectId || '',
            samples: {
                sampleIds: querySampleIds,
                habitatAssay: [habitatAssayType?.key || ''],
            },
        },
        {
            skip: !currentProjectId || !querySampleIds.length || !habitatAssayType,
        }
    );

    if (!querySampleIds.length) {
        return (
            <div className={styles.bottomContentPlaceholder}>
                Select a sample group in the key to view the species within that community
            </div>
        );
    }

    if (isFetching || isFetchingChartData) {
        return <Loader />;
    }

    if (!currentData) {
        return null;
    }

    const handlePopoverOpen = (e: BaseSyntheticEvent, popoverId: string) => {
        setOpenedPopoverId(popoverId);
    };
    const handlePopoverClose = () => {
        setOpenedPopoverId('');
    };

    const { taxonomySummary } = currentData.communityComparisonSamples;
    const maxSampleCount = Math.max(...taxonomySummary.map(entry => entry.sampleCount));
    return (
        <div className={styles.asideBottom}>
            <Table className={styles.speciesListTable} testId={`species-list-table`}>
                <tbody>
                    <tr>
                        <div>
                            <span>Named Species</span>
                            <QuestionButtonHelp type='api' slug='map-named-species' placement='right' />
                        </div>
                        {!selectedSampleId && (
                            <th className='text-right'>
                                Named Species in {selectedSampleGroup}
                                <span className={styles.cube}></span>
                            </th>
                        )}
                    </tr>
                    {taxonomySummary
                        .filter(entry => entry.taxonomy.tax_species)
                        .sort((a, b) => b.sampleCount - a.sampleCount)
                        .map((entry, index) => (
                            <tr key={index} onMouseEnter={e => handlePopoverOpen(e, `pop-${index}`)} onMouseLeave={handlePopoverClose}>
                                <td className={`italic ${!selectedSampleId ? 'max-w-[150px]' : ''}`}>
                                    <Popover open={openedPopoverId === `pop-${index}`}>
                                        <PopoverAnchor asChild>
                                            <span>{entry.taxonomy.tax_species}</span>
                                        </PopoverAnchor>
                                        {entry.taxonomy.tax_species && (
                                            <PopoverContent side='right' align='start' sideOffset={8} className='z-50'>
                                                <SpeciesInfoPopup
                                                    scientificName={entry.taxonomy.tax_species}
                                                    taxonomySummary={taxonomySummary}
                                                />
                                            </PopoverContent>
                                        )}
                                    </Popover>
                                </td>
                                {!selectedSampleId && (
                                    <td>
                                        <PercentageBar
                                            score={entry.sampleCount}
                                            scoreTextPosition={5}
                                            scoreUnit=''
                                            domain={[0, maxSampleCount]}
                                        />
                                    </td>
                                )}
                            </tr>
                        ))}
                </tbody>
            </Table>
        </div>
    );
};

export default AsideBottomContent;
